import { StarFilledIcon, StarIcon } from '@radix-ui/react-icons';
import { Button } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { VideoHelper } from 'lib_ts/classes/video.helper';
import { PitcherHand } from 'lib_ts/enums/pitches.enums';
import { IMachine } from 'lib_ts/interfaces/i-machine';
import { IVideo } from 'lib_ts/interfaces/i-video';
import { useContext, useMemo } from 'react';
import { STATIC_PREFIX } from 'stores/use-videos-store';

export const VideoLibrarySetDefaultButton = (props: { video: IVideo }) => {
  const { machine, update } = useContext(MachineContext);

  const isStatic = props.video.video_path.startsWith(STATIC_PREFIX);

  const isLeft =
    BallHelper.getPitcherHand(props.video.ReleaseSide) === PitcherHand.LHP;

  const isDefault = useMemo(() => {
    if (!props.video) {
      return undefined;
    }

    if (isLeft) {
      return props.video._id === machine.default_video_lhp;
    }

    return props.video._id === machine.default_video_rhp;
  }, [
    props.video,
    isLeft,
    machine.default_video_lhp,
    machine.default_video_rhp,
  ]);

  if (isStatic) {
    return null;
  }

  return (
    <Button
      variant="soft"
      disabled={isDefault}
      title={
        isDefault
          ? `This is already the default video for ${
              isLeft ? PitcherHand.LHP : PitcherHand.RHP
            } on ${machine.machineID}.`
          : `Click to set the default video for ${
              isLeft ? PitcherHand.LHP : PitcherHand.RHP
            } on ${machine.machineID}.`
      }
      onClick={async () => {
        if (isDefault) {
          return;
        }

        const errors = VideoHelper.getErrors(props.video);

        if (errors.length > 0) {
          NotifyHelper.warning({
            message_md: t('videos.default-video-issues-msg'),
          });
          return;
        }

        const payload: Partial<IMachine> = {
          _id: machine._id,
        };

        if (isLeft) {
          payload.default_video_lhp = props.video._id;
        } else {
          payload.default_video_rhp = props.video._id;
        }

        const success = await update(payload);

        if (success) {
          NotifyHelper.info({
            message_md: t('videos.updated-default-video-for-x', {
              x: machine.machineID,
            }),
          });
        }
      }}
    >
      {isDefault ? <StarFilledIcon /> : <StarIcon />}{' '}
      {t('common.set-as-default')}
    </Button>
  );
};
